import { useCallback, useState } from "react";

/**
 * Theme pinning. With nothing stored, `tokens.css` follows `prefers-color-scheme` (the `:not([data-theme="light"])`
 * media query); a stored choice pins `<html data-theme="…">`, which both the explicit `:root[data-theme="dark"]`
 * block and the media-query opt-out honour. Storage is best-effort: private-mode failures leave the session theme
 * applied but unpersisted.
 */
export type ThemeName = "light" | "dark";

const STORAGE_KEY = "lucimig-theme";

export function storedTheme(): ThemeName | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
}

export function systemTheme(): ThemeName {
  // jsdom has no matchMedia — tests and SSR-ish paths fall back to light.
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

/** Called once from `main.tsx` before the first render, so a pinned theme applies without a flash. */
export function applyInitialTheme(): void {
  const pinned = storedTheme();
  if (pinned !== null) document.documentElement.dataset.theme = pinned;
}

export function useTheme(): { effective: ThemeName; toggle: () => void } {
  const [pinned, setPinned] = useState<ThemeName | null>(() => storedTheme());
  const effective = pinned ?? systemTheme();
  const toggle = useCallback(() => {
    const next: ThemeName = effective === "dark" ? "light" : "dark";
    document.documentElement.dataset.theme = next;
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // storage unavailable — the theme still applies for this session
    }
    setPinned(next);
  }, [effective]);
  return { effective, toggle };
}
